import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  ConfigProvider,
  Divider,
  Flex,
  Segmented,
  Tabs,
  theme,
  Typography,
} from '@duxton-web/ui';
import type { ColumnsType } from '@duxton-web/ui/es/table';
import scribe from '@grab-x/scribe-web-sdk';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import {
  ExperimentBackendService,
  ExperimentCity,
  ExperimentCountry,
  ExperimentCreatedAt,
  ExperimentDomain,
  ExperimentDuration,
  ExperimentEndDate,
  ExperimentEvents,
  ExperimentNameColumn,
  ExperimentOwner,
  ExperimentProductAnalyst,
  ExperimentProductManager,
  ExperimentStartDate,
  ExperimentStatus,
  ExperimentTableOptions,
  ExperimentTags,
  ExperimentVariables,
} from './columns';
import ExperimentsModals from './experiments-modals';
import ExperimentsTable from './experiments-table';
import { getExperimentFilters } from './filters';
import { useExperimentsUrlState } from './use-experiments-url-state';
import { AccessControlWrapper, ObjectDetailPage } from '@/common/components';
import { SearchType } from '@/common/components/DashboardTable';
import {
  EXPERIMENTS_PARAM_DEFAULT_OWNER,
  EXPERIMENTS_PARAM_OWNER,
  EXPERIMENTS_PARAM_SEARCH_KEY,
  EXPERIMENTS_TAB_VIEW,
} from '@/common/const';
import { scribeInstrumentationEvents } from '@/common/Scribe/EventsList';
import EnvironmentToggle from '@/components/environment-toggle';
import { AB } from '@/constants';
import Announcements from '@/features/announcement';
import { ExperimentAction } from '@/features/experiment/constants';
import { useFetchExperimentViews } from '@/hooks/usePlannerQueries';
import {
  AdaptiveExperiment,
  Experiment,
  ExperimentView,
  SuperExperiment,
} from '@/lib/App';
import { SELECTED_EXPERIMENT_LS_KEY } from '@/pages/ExperimentFormV2/helper/consts';
import { ANNOUNCEMENTS } from '@/pages/MainLayout/announcements';
import { UserAction } from '@/utils/accessControl';
import { useAppNavigate } from '@/utils/navigation';
import { getLoggedUserEmail } from '@/utils/user';

import '@/pages/ExperimentPageV2/index.css';
import './experiments-listing.css';

import CalendarPageV2 from '@/pages/CalendarPageV2/calendar-page-v2';

const enum ExperimentViewToggle {
  TABLE = 'Table',
  CALENDAR = 'Calendar',
}

const ExperimentsListing: React.FC = () => {
  const { navigateTo } = useAppNavigate();
  const { domainFamily } = useParams<{ domainFamily: string }>();

  const [_selectedExperiment, setSelectedExperiment] =
    useLocalStorage<Experiment>(SELECTED_EXPERIMENT_LS_KEY);

  const [targetExperiment, setTargetExperiment] = useState<Experiment>(null);
  const [targetAdaptiveExperiment, setTargetAdaptiveExperiment] =
    useState<AdaptiveExperiment>(null);
  const [experimentType, setExperimentType] = useState<string>(AB);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openAbortModal, setOpenAbortModal] = useState(false);
  const [openPauseModal, setOpenPauseModal] = useState(false);

  const {
    searchVal,
    pageNumber,
    pageSize,
    tableFilters,
    queryFilters,
    tabView,
    showCalendar,
    updateUrl,
    onTabChange,
    onViewToggle,
  } = useExperimentsUrlState();

  const {
    data: experimentViewsData,
    isFetching: loading,
    refetch: refetchExperiments,
  } = useFetchExperimentViews(queryFilters, pageNumber, pageSize, {
    enabled: !showCalendar,
  });

  const resetTargets = () => {
    setTargetExperiment(null);
    setTargetAdaptiveExperiment(null);
  };

  const handlePause = useCallback((record: SuperExperiment) => {
    if (record?.type === AB)
      setTargetExperiment(record as unknown as Experiment);
    else setTargetAdaptiveExperiment(record as unknown as AdaptiveExperiment);
    setExperimentType(record?.type);
    setOpenPauseModal(true);
    setOpenRejectModal(false);
    setOpenAbortModal(false);
  }, []);

  const handleReject = useCallback((record: SuperExperiment) => {
    if (record?.type === AB)
      setTargetExperiment(record as unknown as Experiment);
    else setTargetAdaptiveExperiment(record as unknown as AdaptiveExperiment);
    setExperimentType(record?.type);
    setOpenRejectModal(true);
    setOpenPauseModal(false);
    setOpenAbortModal(false);
  }, []);

  const handleAbort = useCallback((record: SuperExperiment) => {
    if (record?.type === AB)
      setTargetExperiment(record as unknown as Experiment);
    else setTargetAdaptiveExperiment(record as unknown as AdaptiveExperiment);
    setExperimentType(record?.type);
    setOpenAbortModal(true);
    setOpenRejectModal(false);
    setOpenPauseModal(false);
  }, []);

  const handleEdit = useCallback(
    (record: SuperExperiment) => {
      scribe.sendEvent(scribeInstrumentationEvents.clickEditExperiment.name, {
        experiment: record.name,
        status: record.state,
        STATE_NAME: scribeInstrumentationEvents.clickEditExperiment.state,
      });
      setSelectedExperiment(record as unknown as Experiment);
      navigateTo(
        `/t/experiments/${ExperimentAction.CREATE}?expName=${record.name}&action=edit`,
      );
    },
    [navigateTo, setSelectedExperiment],
  );

  const handleClone = useCallback(
    (record: SuperExperiment) => {
      scribe.sendEvent(scribeInstrumentationEvents.clickCloneExperiment.name, {
        experiment: record.name,
        status: record.state,
        STATE_NAME: scribeInstrumentationEvents.clickCloneExperiment.state,
      });
      setSelectedExperiment(record as unknown as Experiment);
      navigateTo(
        `/t/experiments/${ExperimentAction.CREATE}?expName=${record.name}&action=clone`,
      );
    },
    [navigateTo, setSelectedExperiment],
  );

  const columns: ColumnsType<SuperExperiment> = useMemo(
    () => [
      ExperimentNameColumn,
      ExperimentStatus,
      ExperimentEvents,
      ExperimentDuration,
      ExperimentCreatedAt,
      ExperimentStartDate,
      ExperimentEndDate,
      ExperimentOwner,
      ExperimentProductManager,
      ExperimentProductAnalyst,
      ExperimentTags,
      ExperimentDomain,
      ExperimentVariables,
      ExperimentCountry,
      ExperimentCity,
      ExperimentBackendService,
      ExperimentTableOptions(
        handleReject,
        handleEdit,
        handleAbort,
        handleClone,
        handlePause,
        refetchExperiments,
      ),
    ],
    [
      handleReject,
      handleEdit,
      handleAbort,
      handleClone,
      handlePause,
      refetchExperiments,
    ],
  );

  const superExperiments = useMemo<SuperExperiment[]>(() => {
    return (
      experimentViewsData?.experiment_views?.map(
        (experiment_view: ExperimentView) => {
          const model: SuperExperiment = experiment_view.experiment_model
            ? {
                domain: experiment_view.experiment_model.domain,
                name: experiment_view.experiment_model.name,
                state: experiment_view.experiment_model.state,
                variables: experiment_view.experiment_model.variables,
                constraints: experiment_view.experiment_model.constraints,
                owner: experiment_view.experiment_model.owner,
                isOverlap:
                  experiment_view.experiment_model?.approvalFlow?.data?.length >
                    1 &&
                  experiment_view.experiment_model?.approvalFlow?.resources
                    ?.length > 0,
                slotting: experiment_view.experiment_model.slotting,
                metadata: experiment_view.experiment_model.metadata,
                customTimeSliceStrategy:
                  experiment_view.experiment_model.customTimeSliceStrategy,
                approvalFlow: experiment_view.experiment_model.approvalFlow,
              }
            : {
                domain:
                  experiment_view.adaptive_experiment_model.experiment_template
                    .domain,
                name: experiment_view.adaptive_experiment_model.name,
                state: experiment_view.adaptive_experiment_model.state,
                variables:
                  experiment_view.adaptive_experiment_model.experiment_template
                    .variables,
                constraints:
                  experiment_view.adaptive_experiment_model.experiment_template
                    .constraints,
                owner: experiment_view.adaptive_experiment_model.owner,
                isOverlap: false,
              };
          model.type = experiment_view.type;
          model.created_at = experiment_view.created_at;
          model.start_time = experiment_view.start_time;
          model.end_time = experiment_view.end_time;
          return model;
        },
      ) ?? []
    );
  }, [experimentViewsData]);

  const onSearch = (
    newSearchVal: string,
    newFilters: { [key: string]: any },
    selectedPageNumber: number,
    selectedPageSize: number,
    type: string,
  ) => {
    const totalExperiments = experimentViewsData?.count ?? 0;

    switch (type) {
      case SearchType.PAGE_NUMBER:
        scribe.sendEvent(
          scribeInstrumentationEvents.changeExperimentPageNumber.name,
          {
            page_clicked: selectedPageNumber,
            current_page: pageNumber,
            rows_per_page: pageSize,
            total_pages: Math.ceil(totalExperiments / pageSize),
            total_experiments: totalExperiments,
            STATE_NAME:
              scribeInstrumentationEvents.changeExperimentPageNumber.state,
          },
        );
        break;
      case SearchType.PAGE_SIZE:
        scribe.sendEvent(
          scribeInstrumentationEvents.changeExperimentPageSize.name,
          {
            row_clicked: selectedPageSize,
            current_row: pageSize,
            current_page: pageNumber,
            rows_per_page: selectedPageSize,
            total_pages: Math.ceil(totalExperiments / selectedPageSize),
            total_experiments: totalExperiments,
            STATE_NAME:
              scribeInstrumentationEvents.changeExperimentPageSize.state,
          },
        );
        break;
      case SearchType.FILTER:
        if (Object.keys(newFilters).length === 0) {
          scribe.sendEvent(
            scribeInstrumentationEvents.clickClearFilterExperiment.name,
            {
              ...newFilters,
              STATE_NAME:
                scribeInstrumentationEvents.clickClearFilterExperiment.state,
            },
          );
        } else {
          scribe.sendEvent(
            scribeInstrumentationEvents.clickApplyFilterExperiment.name,
            {
              ...newFilters,
              STATE_NAME:
                scribeInstrumentationEvents.clickApplyFilterExperiment.state,
            },
          );
        }
        break;
      default:
        scribe.sendEvent(
          scribeInstrumentationEvents.clickExperimentSearch.name,
          {
            search_term: newSearchVal,
            STATE_NAME: scribeInstrumentationEvents.clickExperimentSearch.state,
          },
        );
    }

    const finalFilters: { [key: string]: any } = {
      ...newFilters,
      pageSize: selectedPageSize,
      pageNo: selectedPageNumber,
    };

    if (tabView === 'my') {
      finalFilters[EXPERIMENTS_PARAM_DEFAULT_OWNER] = getLoggedUserEmail();
    }

    if (newSearchVal) {
      finalFilters[EXPERIMENTS_PARAM_SEARCH_KEY] = newSearchVal;
    } else {
      delete finalFilters[EXPERIMENTS_PARAM_SEARCH_KEY];
    }

    if (tabView === 'my') {
      finalFilters[EXPERIMENTS_TAB_VIEW] = 'my';
    }

    updateUrl(finalFilters);
  };

  const renderCreateButton = () => (
    <AccessControlWrapper action={UserAction.CREATE_AN_EXPERIMENT}>
      {(hasAccess) => (
        <Button
          size="small"
          type="primary"
          disabled={!hasAccess}
          onClick={() => {
            scribe.sendEvent(
              scribeInstrumentationEvents.clickCreateExperiment.name,
              {
                STATE_NAME:
                  scribeInstrumentationEvents.clickCreateExperiment.state,
              },
            );
            navigateTo(
              `/${domainFamily}/experiments/${ExperimentAction.CREATE}`,
            );
          }}
        >
          Create Experiment
        </Button>
      )}
    </AccessControlWrapper>
  );

  const filtersExcludeFromCount = useMemo(
    () =>
      tabView === 'all'
        ? [
            EXPERIMENTS_TAB_VIEW,
            EXPERIMENTS_PARAM_DEFAULT_OWNER,
            EXPERIMENTS_PARAM_SEARCH_KEY,
            'env',
          ]
        : [
            EXPERIMENTS_TAB_VIEW,
            EXPERIMENTS_PARAM_DEFAULT_OWNER,
            EXPERIMENTS_PARAM_OWNER,
            EXPERIMENTS_PARAM_SEARCH_KEY,
            'env',
          ],
    [tabView],
  );

  const onShowFilterDrawer = () => {
    scribe.sendEvent(
      scribeInstrumentationEvents.clickShowFilterExperiment.name,
      {
        ...tableFilters,
        STATE_NAME: scribeInstrumentationEvents.clickShowFilterExperiment.state,
      },
    );
  };

  const renderContent = () => (
    <Card
      className="experiments-tabs-card"
      style={{ flexGrow: 1 }}
      styles={{
        body: {
          padding: '12px 0px',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div style={{ position: 'relative' }}>
        <Flex align="center" style={{ paddingLeft: 16, paddingRight: 16 }}>
          {!showCalendar && (
            <Tabs
              activeKey={tabView}
              className="experiments-tabs"
              size="small"
              items={[
                { key: 'all', label: 'All Experiments' },
                { key: 'my', label: 'My Experiments' },
              ]}
              onChange={(activeKey) => onTabChange(activeKey as 'all' | 'my')}
            />
          )}

          <div style={{ marginLeft: 'auto' }}>
            <ConfigProvider
              theme={{
                components: {
                  Segmented: {
                    trackBg: theme.getDesignToken().white,
                    motionDurationFast: '0',
                  },
                },
              }}
            >
              <Segmented
                size="small"
                options={[
                  ExperimentViewToggle.TABLE,
                  ExperimentViewToggle.CALENDAR,
                ]}
                value={
                  showCalendar
                    ? ExperimentViewToggle.CALENDAR
                    : ExperimentViewToggle.TABLE
                }
                onChange={(value) => {
                  if (value === ExperimentViewToggle.CALENDAR) {
                    scribe.sendEvent(
                      scribeInstrumentationEvents.viewNewCalendarPage.name,
                    );
                  }
                  onViewToggle(value === ExperimentViewToggle.CALENDAR);
                }}
              />
            </ConfigProvider>
          </div>
        </Flex>

        <Divider
          style={{
            position: 'absolute',
            bottom: '0px',
            left: '0px',
            right: '0px',
            margin: 0,
            zIndex: 10,
          }}
        />
      </div>

      {showCalendar ? (
        <CalendarPageV2 rightActions={[renderCreateButton()]} />
      ) : (
        <ExperimentsTable
          id={
            tabView === 'all'
              ? 'experiment-experiments'
              : 'experiment-experiments-my'
          }
          tabView={tabView}
          columns={columns}
          dataSource={superExperiments}
          filters={tableFilters}
          filterFields={getExperimentFilters(tabView)}
          searchVal={searchVal}
          pageNumber={pageNumber}
          pageSize={pageSize}
          total={experimentViewsData?.count}
          loading={loading}
          filtersExcludeFromCount={filtersExcludeFromCount}
          rightActions={[renderCreateButton()]}
          onSearch={onSearch}
          onFilterClick={onShowFilterDrawer}
          onColumnsRefresh={(visibleColumns) => {
            scribe.sendEvent(
              scribeInstrumentationEvents.updateExperimentColumnHeader.name,
              {
                all_columns: visibleColumns.map((c) => c.key),
                STATE_NAME:
                  scribeInstrumentationEvents.updateExperimentColumnHeader
                    .state,
              },
            );
          }}
        />
      )}
    </Card>
  );

  return (
    <ObjectDetailPage id="objectDetailPage-experiments">
      <Flex vertical gap={16} style={{ height: '100%', padding: 24 }}>
        <Announcements id="grabx-announcements" items={ANNOUNCEMENTS} />
        <Flex align="center" justify="space-between">
          <Flex gap={8} align="center">
            <ConfigProvider
              theme={{
                components: {
                  Typography: { titleMarginBottom: 0 },
                  Segmented: {
                    trackBg: theme.getDesignToken().white,
                    motionDurationFast: '0',
                  },
                },
              }}
            >
              <Typography.Title level={3}>Experiments</Typography.Title>
              <EnvironmentToggle />
            </ConfigProvider>
          </Flex>
        </Flex>
        {renderContent()}
      </Flex>

      <ExperimentsModals
        openPauseModal={openPauseModal}
        openRejectModal={openRejectModal}
        openAbortModal={openAbortModal}
        experiment={targetExperiment}
        adaptiveExperiment={targetAdaptiveExperiment}
        experimentType={experimentType}
        onClosePause={() => {
          resetTargets();
          setOpenPauseModal(false);
        }}
        onCloseReject={() => {
          resetTargets();
          setOpenRejectModal(false);
        }}
        onCloseAbort={() => {
          resetTargets();
          setOpenAbortModal(false);
        }}
        onSuccess={() => refetchExperiments()}
      />
    </ObjectDetailPage>
  );
};

export default ExperimentsListing;
